import React, { useEffect, useRef, useMemo } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { PhoneInput, defaultCountries, parseCountry } from 'react-international-phone';
import './react-international-phone.css';
import { ValidationError } from '..';

import css from './FieldPhoneNumber.module.css';

const PREFFERED_CODES = ['nl', 'be', 'de'];

// ISO2 codes for all European countries (you can adjust as needed)
const EUROPE_CODES = [
  'al',
  'ad',
  'at',
  'by',

  'ba',
  'bg',
  'hr',
  'cy',
  'cz',
  'dk',
  'ee',
  'fi',
  'fr',

  'gr',
  'hu',
  'is',
  'ie',
  'it',
  'lv',
  'li',
  'lt',
  'lu',
  'mt',
  'md',
  'mc',
  'me',

  'mk',
  'no',
  'pl',
  'pt',
  'ro',
  'ru',
  'sm',
  'rs',
  'sk',
  'si',
  'es',
  'se',
  'ch',
  'tr',
  'ua',
  'gb',
  'va',
];

const FieldPhoneNumberComponent = props => {
  const {
    rootClassName,
    className,
    selectClassName,
    id,
    label,
    input,
    meta,
    onChange,
    onMount,
    ...rest
  } = props;

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const ref = useRef();
  const { invalid, touched, error } = meta;
  const hasError = touched && invalid && error;

  // Build, sort and memoize the country list:
  const countries = useMemo(() => {
    const preffered = defaultCountries.filter(c => {
      const parsed = parseCountry(c);
      return PREFFERED_CODES.includes(parsed.iso2);
    });
    // 2. Split into Europe vs. others
    const europe = defaultCountries.filter(c => {
      const parsed = parseCountry(c);
      return EUROPE_CODES.includes(parsed.iso2);
    });
    const others = defaultCountries.filter(c => {
      const parsed = parseCountry(c);
      return ![...PREFFERED_CODES, ...EUROPE_CODES].includes(parsed.iso2);
    });

    // 3. Return Europe first, then the rest
    return [...preffered, ...europe, ...others];
  }, []);

  // Handle changes: propagate to Final Form and callback
  const handleChange = e => {
    input.onChange(e);
    if (onChange) {
      onChange(e?.currentTarget?.value, ref.current?.state?.country?.dialCode);
    }
  };

  // On mount, notify initial dial code
  useEffect(() => {
    onMount?.(ref.current?.state?.country?.dialCode);
  }, [onMount]);

  const inputClasses = classNames(selectClassName, { [css.inputError]: hasError });
  const rootClasses = classNames(rootClassName || css.root, className);

  const inputProps = { className: inputClasses, id, ...input, onChange: handleChange, ...rest };

  return (
    <div className={rootClasses}>
      {label && <label htmlFor={id}>{label}</label>}
      <PhoneInput
        ref={ref}
        {...inputProps}
        value={input.value}
        className={css.phoneNumber}
        inputClassName={classNames(css.phoneNumber, { [css.inputError]: hasError })}
        defaultCountry="nl"
        countries={countries}
      />
      <ValidationError fieldMeta={meta} />
    </div>
  );
};

/**
 * Final Form Field wrapping <select> input
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.selectClassName add more style rules to <select> component
 * @param {string} props.name Name of the input in Final Form
 * @param {string} props.id Label is optional, but if it is given, an id is also required so the label can reference the input in the `for` attribute
 * @param {ReactNode} props.label
 * @param {ReactNode} props.children
 * @returns {JSX.Element} Final Form Field containing <select> input
 */
const FieldPhoneNumber = props => {
  return <Field component={FieldPhoneNumberComponent} {...props} />;
};

export default FieldPhoneNumber;
